import { isNil } from "lodash";
import objectToQueryParams from "./objectToQueryParams";

/**
 * Helpers puros do contrato da API da Apuração CAPEX (`v1/apuracaocapex`):
 * base por empresa, paginação do Laravel (`data`+`meta`), `filters[coluna]=a;b`
 * e `sort[coluna]=asc|desc`. Nada aqui faz requisição.
 */

/**
 * Normaliza um filtro de texto vindo da tela. Os campos são `clearable`: ao limpar, o
 * Vuetify define `null` e o `trim()` estourava no `watch`, congelando a grade.
 *
 * @param {*} valor
 * @returns {string}
 */
export const textoFiltro = (valor) => String(isNil(valor) ? "" : valor).trim();

/** Base das rotas do módulo (`empresaId` = `clientId` do Vuex). */
export const baseApuracaoCapex = (empresaId) => `v1/apuracaocapex/${empresaId}`;

/**
 * Converte o `sort` do `v-data-table` (`[{ field, desc }]`) no formato da API
 * (`{ campo: "asc" | "desc" }`).
 *
 * @param {Array<{field: string, desc: boolean}>} [sort]
 * @returns {{ [campo: string]: "asc" | "desc" }}
 */
export const paraSort = (sort) => (sort || []).reduce((acumulado, item) => {
  if (!item || !item.field) return acumulado;

  return { ...acumulado, [item.field]: item.desc ? "desc" : "asc" };
}, {});

/**
 * Query string no padrão da API: `filters` é o mapa `{ coluna: termo | termos[] }`
 * (os termos viram `a;b`) e `sort` pode vir direto do `v-data-table`.
 *
 * @param {{ filters?: object, sort?: Array|object, [chave: string]: any }} params
 * @returns {string}
 */
export const montarQuery = ({ filters = {}, sort = [], ...params } = {}) =>
  objectToQueryParams({
    ...params,
    filters,
    sort: Array.isArray(sort) ? paraSort(sort) : sort,
  });

/**
 * URL de uma rota do módulo, com a query só quando existe (evita `?` solto).
 *
 * @param {number|string} empresaId
 * @param {string} caminho
 * @param {object} [params] mesmos parâmetros de `montarQuery`
 * @returns {string}
 */
export const montarUrl = (empresaId, caminho, params) => {
  const query = montarQuery(params);

  return `${baseApuracaoCapex(empresaId)}/${caminho}${query ? `?${query}` : ""}`;
};

/**
 * Normaliza uma resposta paginada do Laravel (`data` + `meta`) no formato das telas
 * (`rows`/`total`/`page`/`perPage` + `totais`).
 *
 * @param {object} corpo corpo da resposta
 * @param {{ perPage?: number }} [opcoes]
 * @returns {{ rows: Array, total: number, page: number, perPage: number, totais: object }}
 */
export const normalizarPagina = (corpo, { perPage = 15 } = {}) => {
  const meta = (corpo && corpo.meta) || {};
  const linhas = (corpo && corpo.data) || [];

  return {
    rows: linhas,
    total: Number(meta.total !== undefined ? meta.total : linhas.length) || 0,
    page: Number(meta.current_page) || 1,
    perPage: Number(meta.per_page) || perPage,
    totais: (corpo && corpo.totais) || {},
  };
};

export default { baseApuracaoCapex, paraSort, montarQuery, montarUrl, normalizarPagina, textoFiltro };
